import Papa from "papaparse";
import type { ColumnDef } from "../columns";
import { resolveAccessor } from "../columns";
import { formatCell, type FormatterBundle } from "../lib/format";

/**
 * MIME type used for CSV blobs. Matches RFC 4180.
 * UTF-8 is encoded by prepending a BOM so Excel opens the file with correct
 * accents on the Windows desktop client (which still defaults to ANSI when
 * no BOM is present, mangling Cyrillic/Serbian-Latin diacritics).
 */
export const CSV_MIME_TYPE = "text/csv;charset=utf-8";

/** Byte-order mark — Excel-on-Windows opens the CSV as UTF-8 only when present. */
const UTF8_BOM = "\uFEFF";

/**
 * Options accepted by {@link toCsv}.
 *
 * Locale + formatter bundle are injected (instead of imported) so the export
 * helper stays decoupled from `@aio/platform-i18n` — tests can pass mocks and
 * production callers can wire the real bundle. See {@link FormatterBundle}.
 */
export interface ToCsvOptions<TRow> {
  /** Row data in display order (sort/filter already applied by caller). */
  rows: TRow[];
  /** Column definitions. Hidden columns must be omitted by the caller. */
  columns: ColumnDef<TRow>[];
  /** Locale tag passed to formatters (e.g. "sr-Latn", "en-GB"). */
  locale: string;
  /** Formatter bundle resolved from `@aio/platform-i18n` or test mocks. */
  formatters: FormatterBundle;
  /**
   * Field delimiter override. Defaults to "," to match RFC 4180. Some Excel
   * locales prefer ";" — callers route the user's preference through here
   * rather than letting the export helper guess.
   */
  delimiter?: string;
}

/**
 * Serialize rows to a CSV blob.
 *
 * Strategy:
 * 1. Build a header row from `column.header`.
 * 2. For each row, run the column's accessor and dispatch through
 *    {@link formatCell} so the on-disk representation matches what the user
 *    sees in the table (currency symbols, date format, percent sign, …).
 * 3. Hand the 2-D array to `papaparse.unparse`, which handles RFC 4180 quoting
 *    (embedded delimiters, embedded newlines, embedded double-quotes).
 * 4. Prepend a UTF-8 BOM and wrap in a Blob so the caller can hand it to
 *    `URL.createObjectURL` without further processing.
 *
 * Rationale for not exporting the raw value: a money column with `1234.5`
 * would round-trip back through Excel as `1234.5` rather than the formatted
 * `€1.234,50`. The user expects what they see; format-aware export is the
 * right default. Callers wanting raw values can declare their own `format`
 * on the column or build the CSV by hand.
 */
export function toCsv<TRow>(options: ToCsvOptions<TRow>): Blob {
  const { rows, columns, locale, formatters, delimiter = "," } = options;

  // Header row — column.header is either a literal string or an i18n key. We
  // emit whatever the caller stored; resolution to a translated string is the
  // caller's responsibility (matches FilterBar's chip behaviour).
  const header = columns.map((c) => c.header);

  const data: string[][] = rows.map((row) => {
    return columns.map((column) => {
      const accessor = resolveAccessor(column);
      const value = accessor(row);
      return formatCell(value, column, locale, formatters, { row });
    });
  });

  // papaparse.unparse handles all RFC 4180 quoting concerns — embedded
  // delimiters, newlines, quotes — and the `delimiter` option lets locales
  // that expect ";" route through here.
  const csv = Papa.unparse([header, ...data], {
    delimiter,
    newline: "\r\n",
    quotes: true,
  });

  return new Blob([UTF8_BOM, csv], { type: CSV_MIME_TYPE });
}
